import { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { apartments } from '@/mocks/apartments';
import { blogArticles } from '@/mocks/blog';
import { businesses } from '@/mocks/businesses';
import { freelances, formations, emplois, boutiques, utilisateurs } from '@/mocks/searchData';

interface GroupedResult {
  type: string;
  icon: string;
  label: string;
  items: { id: string; title: string; subtitle: string; path: string; meta?: string }[];
}

const searchTypes = [
  { key: 'all', label: 'Tout', icon: 'ri-global-line' },
  { key: 'produits', label: 'Produits', icon: 'ri-shopping-bag-3-line' },
  { key: 'services', label: 'Services', icon: 'ri-tools-line' },
  { key: 'freelances', label: 'Freelances', icon: 'ri-briefcase-4-line' },
  { key: 'formations', label: 'Formations', icon: 'ri-graduation-cap-line' },
  { key: 'emplois', label: 'Emplois', icon: 'ri-id-card-line' },
  { key: 'appartements', label: 'Appartements', icon: 'ri-home-4-line' },
  { key: 'articles', label: 'Articles', icon: 'ri-article-line' },
  { key: 'utilisateurs', label: 'Utilisateurs', icon: 'ri-user-3-line' },
  { key: 'boutiques', label: 'Boutiques', icon: 'ri-store-3-line' },
];

function SearchPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [activeType, setActiveType] = useState('all');

  useEffect(() => {
    setQuery(searchParams.get('q') || '');
  }, [searchParams]);

  const results = useMemo<GroupedResult[]>(() => {
    const q = query.trim().toLowerCase();
    if (!q) return [];

    const match = (text: string) => text.toLowerCase().includes(q);

    const groups: GroupedResult[] = [];

    // Produits & Services (from businesses directory)
    const produits = businesses
      .filter((b) => match(b.name) || match(b.categoryName) || match(b.description))
      .map((b) => ({
        id: b.id,
        title: b.name,
        subtitle: b.categoryName,
        path: `/business/${b.slug}`,
        meta: b.city,
      }));
    const services = businesses
      .filter((b) => match(b.name) || match(b.categoryName))
      .map((b) => ({
        id: `svc-${b.id}`,
        title: b.name,
        subtitle: b.description.slice(0, 60) + '...',
        path: `/business/${b.slug}`,
        meta: b.city,
      }));

    const fre = freelances
      .filter((f) => match(f.name) || match(f.title) || match(f.skills))
      .map((f) => ({ id: f.id, title: f.name, subtitle: f.title, path: f.path, meta: f.rate }));

    const forr = formations
      .filter((f) => match(f.name) || match(f.provider))
      .map((f) => ({ id: f.id, title: f.name, subtitle: f.provider, path: f.path, meta: f.price }));

    const emp = emplois
      .filter((e) => match(e.name) || match(e.company))
      .map((e) => ({ id: e.id, title: e.name, subtitle: e.company, path: e.path, meta: `${e.city} · ${e.type}` }));

    const apt = apartments
      .filter((a) => match(a.title) || match(a.quartier) || match(a.type))
      .map((a) => ({
        id: a.id,
        title: a.title,
        subtitle: `${a.quartier}, ${a.city}`,
        path: `/apartments/${a.id}`,
        meta: `${a.price.toLocaleString('fr-FR')} FCFA`,
      }));

    const art = blogArticles
      .filter((a) => match(a.title) || match(a.excerpt) || a.tags.some((t) => match(t)))
      .map((a) => ({ id: a.id, title: a.title, subtitle: a.category, path: `/blog/${a.slug}`, meta: `${a.readTime} min` }));

    const users = utilisateurs
      .filter((u) => match(u.name) || match(u.role))
      .map((u) => ({ id: u.id, title: u.name, subtitle: u.role, path: u.path, meta: u.location }));

    const shops = boutiques
      .filter((b) => match(b.name) || match(b.seller))
      .map((b) => ({ id: b.id, title: b.name, subtitle: b.seller, path: b.path, meta: `${b.products} produits` }));

    const push = (type: string, label: string, icon: string, items: GroupedResult['items']) => {
      if (items.length > 0) groups.push({ type, label, icon, items });
    };

    push('produits', 'Produits', 'ri-shopping-bag-3-line', produits);
    push('services', 'Services', 'ri-tools-line', services);
    push('freelances', 'Freelances', 'ri-briefcase-4-line', fre);
    push('formations', 'Formations', 'ri-graduation-cap-line', forr);
    push('emplois', 'Emplois', 'ri-id-card-line', emp);
    push('appartements', 'Appartements', 'ri-home-4-line', apt);
    push('articles', 'Articles', 'ri-article-line', art);
    push('utilisateurs', 'Utilisateurs', 'ri-user-3-line', users);
    push('boutiques', 'Boutiques', 'ri-store-3-line', shops);

    return groups;
  }, [query]);

  const filteredResults = activeType === 'all'
    ? results
    : results.filter((g) => g.type === activeType);

  const totalCount = results.reduce((acc, g) => acc + g.items.length, 0);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearchParams(query.trim() ? { q: query.trim() } : {});
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero / Search bar */}
      <section className="pt-24 md:pt-32 pb-8">
        <div className="w-full px-4 md:px-8 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block text-sm font-semibold text-primary-400 uppercase tracking-wider mb-3">
              Recherche globale
            </span>
            <h1 className="font-heading font-bold text-3xl md:text-5xl text-foreground-50 mb-4">
              Explorez tout l'écosystème
            </h1>
            <p className="text-base md:text-lg text-foreground-400 max-w-2xl mx-auto mb-8">
              Produits, services, freelances, formations, emplois, appartements, articles et bien plus.
            </p>

            <form onSubmit={handleSearch} className="max-w-2xl mx-auto">
              <div className="flex items-center bg-background-100/80 backdrop-blur-md border border-background-300/60 rounded-lg overflow-hidden">
                <div className="w-12 h-12 flex items-center justify-center shrink-0">
                  <i className="ri-search-line text-lg text-foreground-500" />
                </div>
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher dans tout WorldApeiron..."
                  className="flex-1 bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 py-3 outline-none"
                />
                <button
                  type="submit"
                  className="px-5 py-3 bg-primary-500 text-foreground-950 font-semibold text-sm hover:bg-primary-400 transition-colors shrink-0 whitespace-nowrap"
                >
                  Rechercher
                </button>
              </div>
            </form>
          </div>
        </div>
      </section>

      {/* Type filters */}
      {results.length > 0 && (
        <section className="w-full px-4 md:px-8 lg:px-12 pb-6">
          <div className="flex flex-wrap justify-center gap-2">
            {searchTypes.map((t) => {
              const count = t.key === 'all'
                ? totalCount
                : results.find((g) => g.type === t.key)?.items.length || 0;
              return (
                <button
                  key={t.key}
                  onClick={() => setActiveType(t.key)}
                  className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors whitespace-nowrap flex items-center gap-1.5 ${
                    activeType === t.key
                      ? 'bg-primary-500 text-foreground-950 border-primary-500'
                      : 'bg-background-100 text-foreground-500 border-background-300/60 hover:border-primary-400/40'
                  }`}
                >
                  <i className={t.icon} />
                  {t.label}
                  {count > 0 && <span className="text-xs opacity-70">({count})</span>}
                </button>
              );
            })}
          </div>
        </section>
      )}

      {/* Results */}
      <section className="w-full px-4 md:px-8 lg:px-12 pb-20">
        <div className="max-w-4xl mx-auto">
          {query.trim() === '' ? (
            <div className="text-center py-20">
              <div className="w-20 h-20 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-search-2-line text-3xl text-foreground-400" />
              </div>
              <p className="text-foreground-500 font-medium mb-2">Commencez à taper pour rechercher</p>
              <p className="text-sm text-foreground-500 max-w-md mx-auto">
                Recherchez des produits, services, freelances, formations, emplois, appartements, articles, utilisateurs ou boutiques.
              </p>
            </div>
          ) : filteredResults.length === 0 ? (
            <div className="text-center py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 mb-4">
                <i className="ri-emotion-sad-line text-2xl text-foreground-400" />
              </div>
              <p className="text-foreground-500 font-medium mb-2">Aucun résultat pour « {query} »</p>
              <p className="text-sm text-foreground-500">Vérifiez l'orthographe ou essayez d'autres mots-clés.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {filteredResults.map((group) => (
                <div key={group.type}>
                  <h2 className="flex items-center gap-2 font-heading font-bold text-lg text-foreground-50 mb-4">
                    <span className="w-9 h-9 flex items-center justify-center rounded-lg bg-primary-500/10">
                      <i className={`${group.icon} text-primary-400`} />
                    </span>
                    {group.label}
                    <span className="text-sm font-normal text-foreground-400">({group.items.length})</span>
                  </h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {group.items.map((item) => (
                      <Link
                        key={item.id}
                        to={item.path}
                        className="flex items-start gap-3 p-4 rounded-xl bg-background-100 border border-background-200/70 hover:border-primary-400/40 transition-all"
                      >
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-background-200/70 shrink-0">
                          <i className={`${group.icon} text-lg text-foreground-400`} />
                        </div>
                        <div className="min-w-0">
                          <span className="block font-semibold text-sm text-foreground-50 leading-snug line-clamp-1">
                            {item.title}
                          </span>
                          <span className="block text-xs text-foreground-400 line-clamp-1">{item.subtitle}</span>
                          {item.meta && (
                            <span className="block text-xs text-primary-400 mt-1">{item.meta}</span>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}

export default SearchPage;